<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import PrimeDatePicker from 'primevue/datepicker'
import { useFormFieldContext } from '@uiKit/composables/useFormFieldContext'

interface Props {
  /** Bound value. `null` clears the input. */
  modelValue?: Date | null
  /** PrimeVue date format token (see PrimeVue DatePicker docs). */
  dateFormat?: string
  /** Empty-state placeholder text. */
  placeholder?: string
  /** Disable user interaction. */
  disabled?: boolean
  /** Render the value but block editing — greyed read-only look. */
  readonly?: boolean
  /** Apply the invalid-state styling. */
  invalid?: boolean
  /** Render the calendar trigger icon inside the input. */
  showIcon?: boolean
  /** Earliest selectable date (inclusive). */
  minDate?: Date
  /** Latest selectable date (inclusive). */
  maxDate?: Date
  /**
   * Allow typing a date. Off makes the input open the calendar only — a partly
   * typed value (e.g. "2026-10-1") would otherwise parse as a different day.
   */
  manualInput?: boolean
  /**
   * Where the calendar panel mounts. `self` (default) keeps it beside the input
   * (BUG-025). Inside a scrolling container that clips — a dialog body — pass
   * `body` so the panel floats above it.
   */
  appendTo?: 'self' | 'body'
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  // "22 Oct 2026" — unambiguous across day-first / month-first readers (BUG-036).
  dateFormat: 'dd M yy',
  showIcon: true,
  disabled: false,
  readonly: false,
  invalid: false,
  manualInput: true,
  appendTo: 'self',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | null): void
}>()

const field = useFormFieldContext()

// PrimeVue only closes the panel on Escape while focus is inside it; once the
// operator clicks a day cell or the header, Escape did nothing (BUG-025). Listen
// at document level while open. `overlayVisible` is the component's own state —
// it exposes no public hide() method.
const pickerRef = ref<{ overlayVisible: boolean } | null>(null)

function handleEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !pickerRef.value) return
  pickerRef.value.overlayVisible = false
}

function handleShow() {
  document.addEventListener('keydown', handleEscape)
}

function handleHide() {
  document.removeEventListener('keydown', handleEscape)
}

onBeforeUnmount(handleHide)
</script>

<template>
  <PrimeDatePicker
    ref="pickerRef"
    :inputId="field?.controlId"
    :ariaLabelledby="field?.labelId"
    :modelValue="props.modelValue"
    :dateFormat="props.dateFormat"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :invalid="props.invalid"
    :showIcon="props.showIcon"
    :minDate="props.minDate"
    :maxDate="props.maxDate"
    :manualInput="props.manualInput"
    iconDisplay="input"
    :appendTo="props.appendTo"
    class="app-datepicker"
    inputClass="app-datepicker-field"
    @update:modelValue="emit('update:modelValue', $event as Date | null)"
    @show="handleShow"
    @hide="handleHide"
  />
</template>

<style>
/* Unscoped on purpose -- targets PrimeVue inner / teleported DOM that scoped CSS cannot reach. */
/* Positioning context for the panel appended to "self" (BUG-025). */
.app-datepicker {
  position: relative;
  width: 100%;
}

.app-datepicker-field {
  width: 100%;
  height: 42px;
  border-radius: 6px;
  border: 1px solid var(--p-surface-300);
  background: var(--p-surface-0);
  font-size: 0.9375rem;
  color: var(--p-text-color);
  padding: 0 0.75rem;
  font-family: inherit;
}

.app-datepicker-field:not(:disabled):not([readonly]):hover {
  border-color: var(--p-primary-300);
}

/* Invalid wins over hover — the red border must not vanish under the cursor (BUG-024). */
.app-datepicker.p-invalid .app-datepicker-field,
.app-datepicker.p-invalid .app-datepicker-field:not(:disabled):not([readonly]):hover {
  border-color: var(--p-danger-600);
}

.app-datepicker-field:not([readonly]):focus {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 1px var(--p-primary-400);
  outline: none;
}

/* Read-only mirrors the disabled look — greyed surface, muted text, no caret. */
.app-datepicker-field[readonly] {
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
  border-color: var(--p-surface-200);
  cursor: not-allowed;
}

.app-datepicker .p-datepicker-trigger {
  color: var(--p-text-muted-color);
}
</style>
